import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@vehix/portal/components/ui/button';
import { Badge } from '@vehix/portal/components/ui/badge';
import { Input } from '@vehix/portal/components/ui/input';
import { Label } from '@vehix/portal/components/ui/label';
import { Textarea } from '@vehix/portal/components/ui/textarea';
import { Checkbox } from '@vehix/portal/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@vehix/portal/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@vehix/portal/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@vehix/portal/components/ui/select';
import { useToast } from '@vehix/shared/admin/portal-bridge';
import { StatCard } from './dashboard/StatCard';
import {
  List,
  Map as MapIcon,
  MapPin,
  Plus,
  Edit,
  Trash2,
  Search,
  CheckCircle,
  XCircle,
  Loader2,
  Hash,
  X,
  AlertTriangle,
  Download,
  Info,
  ChevronLeft,
  ChevronRight,
  Copy,
  RefreshCw,
  Building2,
  CircleSlash
} from 'lucide-react';
import { tenantAPI, Zone, CreateZoneData, UpdateZoneData } from '@vehix/portal/services/api/tenants';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@vehix/shared/admin/portal-bridge';
import {
  useZones,
  useZoneMutations,
  useRefreshZoneRecallCounts,
  useCurrentRecallCounts,
  useZoneTenantMappings,
  zoneKeys
} from '../hooks/useZones';
import * as XLSX from 'xlsx';
import { Popover, PopoverContent, PopoverTrigger } from '@vehix/portal/components/ui/popover';
import { getZoneColor, getZoneSizeLabel, getRecallCountFromRPC } from '../utils/zoneStatistics';
import { SystemRole } from '@vehix/portal/types';
import { KNOWN_OEMS } from '@vehix/portal/constants/oems';
import { getOemHexColor, getOemTheme } from '../utils/oemColors';

// Manual Zone Management Component (existing functionality)
// Uses React Query for caching and performance (same pattern as useCampaigns and useDealers)
interface ManualZoneTabProps {
  /** Optional view-toggle state — when provided, the toolbar renders a List/Map segmented control. */
  view?: 'list' | 'map';
  onViewChange?: (view: 'list' | 'map') => void;
}

const ManualZoneTab: React.FC<ManualZoneTabProps> = ({ view, onViewChange }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { session, hasSystemRole } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [regionFilter, setRegionFilter] = useState<string>('all');
  const [oemFilter, setOemFilter] = useState<string>('all'); // NEW: OEM filter
  const [selectedZones, setSelectedZones] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);
  const [loadingZipCodes, setLoadingZipCodes] = useState<Set<string>>(new Set());

  // Pagination state — defaults to 100 per page for fewer page changes
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const ZONES_PER_PAGE = pageSize;

  // Load ALL zones once via the shared 10-min cache. Filtering, searching,
  // and pagination all happen client-side over the loaded array — every
  // keystroke and filter change is an in-memory re-render (<16ms), no
  // network round-trip. Same pattern as TenantSetupTab and UserApprovalsTab.
  // The `useZones` hook also feeds the Zone Map page, so users who visit
  // both pages effectively share one cache entry.
  const {
    data: allZonesData,
    isLoading: loading,
    error: zonesError,
    isFetching: loadingMore
  } = useZones();

  // All zones from the cache, plus the client-side filter/search pipeline.
  // Each filter is applied as a separate `useMemo` step so React Query and
  // React both keep stable references when only one filter changes.
  const allZones = useMemo(() => allZonesData ?? [], [allZonesData]);

  const filteredZones = useMemo(() => {
    let result = allZones;
    if (oemFilter !== 'all') {
      result = result.filter(z => z.oem === oemFilter);
    }
    if (regionFilter !== 'all') {
      result = result.filter(z => z.region === regionFilter);
    }
    if (statusFilter !== 'all') {
      result = result.filter(z => z.status === statusFilter);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      result = result.filter(
        z =>
          z.zone_name?.toLowerCase().includes(q) ||
          z.zone_slug?.toLowerCase().includes(q) ||
          z.region?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [allZones, oemFilter, regionFilter, statusFilter, searchTerm]);

  // Pagination is now also client-side: slice the filtered array. This
  // makes page changes literally instant (no network call).
  const totalZones = filteredZones.length;
  const totalPages = Math.max(1, Math.ceil(totalZones / ZONES_PER_PAGE));
  const zones = useMemo(() => {
    const start = (page - 1) * ZONES_PER_PAGE;
    return filteredZones.slice(start, start + ZONES_PER_PAGE);
  }, [filteredZones, page, ZONES_PER_PAGE]);
  const hasMore = page < totalPages;

  // Reset to page 1 when filters/search/page-size narrow the result set,
  // so the user doesn't end up on an empty page after a search.
  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, regionFilter, oemFilter, pageSize]);

  // Stable reference per render of the same zones array — keeps downstream
  // hooks (useCurrentRecallCounts) from seeing a new array identity on
  // every render of an unchanged page. Recall counts are fetched per-page
  // (heavy aggregation RPC) — only the visible page's zones need them for
  // the table cells.
  const zoneIds = useMemo(() => zones.map(z => z.id), [zones]);

  // Single RPC: fetch recall counts + data collection zones together for consistency.
  //
  // We pass no tenant id: the server resolves each zone's assigned tenant and counts
  // against that tenant's own crawl data, falling back to every tenant for an unassigned
  // zone. That is what makes this number agree with the dealer's recall map. Counting all
  // tenants here used to attribute other dealers' recalls to the zone — a zone's ZIPs
  // overlap several crawl footprints, so Dayton showed 105 recalls belonging to a
  // different dealer while its own tenant saw 368.
  const { data: recallData } = useCurrentRecallCounts(zoneIds);
  const recallCounts = recallData?.counts;
  const dataCollectionZones = recallData?.dataCollectionZones;
  const snapshotDates = recallData?.snapshotDates;
  const oldestSnapshotDates = recallData?.oldestSnapshotDates;

  // Tenant mappings are fetched for ALL zones (not just the visible page)
  // because the Assigned/Unassigned KPI cards need accurate cross-page totals.
  // The hook canonicalizes its input set (sorted+deduped queryKey) so this
  // call is cached across renders even when individual filters change.
  const allZoneIds = useMemo(() => allZones.map(z => z.id), [allZones]);
  const { data: tenantMappings } = useZoneTenantMappings(allZoneIds);

  // KPI derivations from the cached all-zones data. We restrict to ACTIVE
  // zones because inactive ones aren't in service — counting them as
  // assigned/unassigned would mislead the admin about coverage health.
  const activeZones = useMemo(
    () => allZones.filter(z => z.status === 'active'),
    [allZones]
  );
  const assignedZoneCount = useMemo(() => {
    if (!tenantMappings) return 0;
    return activeZones.filter(z => tenantMappings[z.id]).length;
  }, [activeZones, tenantMappings]);
  const unassignedZoneCount = activeZones.length - assignedZoneCount;

  // OEM distribution: groupBy active zones to feed the stacked-bar visual.
  // Sorted by descending count so the largest segments anchor the left
  // (matches reading order; biggest brand is the first thing the eye lands on).
  const oemDistribution = useMemo(() => {
    const counts = new globalThis.Map<string, number>();
    activeZones.forEach(z => {
      const oem = z.oem || 'Other';
      counts.set(oem, (counts.get(oem) ?? 0) + 1);
    });
    const total = activeZones.length || 1;
    return Array.from(counts.entries())
      .sort(([, a], [, b]) => b - a)
      .map(([oem, count]) => ({
        oem,
        count,
        percent: (count / total) * 100,
        color: getOemHexColor(oem)
      }));
  }, [activeZones]);

  // Helper to get recall count (OEM-scoped, and tenant-scoped by the server when the
  // zone is assigned — see the useCurrentRecallCounts call above).
  const getRecallCount = (zone: Zone): number | null => {
    if (recallCounts && zone.id in recallCounts) {
      return recallCounts[zone.id] ?? null;
    }
    return zone.total_recalls_current ?? null;
  };

  /**
   * The crawl window behind a zone's count.
   *
   * `asOf` is the cutoff the count was taken at — the newest crawl among this zone's
   * material feeds, for this zone's tenant. It is NOT "the last time we crawled": it can
   * move *earlier* when a zone's tenant changes, because a different tenant's feeds have
   * their own crawl dates.
   *
   * `evictedFeed` is the signal worth acting on. When the oldest contributing crawl is
   * older than the cutoff, at least one of the zone's feeds was left behind by it, so part
   * of the territory is being counted from older data or not counted at all. Currently
   * true for 25 of the 122 assigned zones, with gaps up to 244 days.
   */
  const getCrawlWindow = (
    zone: Zone
  ): { asOf: string; oldest?: string; gapDays?: number; evictedFeed: boolean } | null => {
    const asOf = snapshotDates?.[zone.id] ?? zone.latest_snapshot_date;
    if (!asOf) return null;
    const oldest = oldestSnapshotDates?.[zone.id] ?? zone.oldest_snapshot_date;
    if (!oldest || oldest === asOf) return { asOf, evictedFeed: false };
    const gapDays = Math.round(
      (new Date(asOf).getTime() - new Date(oldest).getTime()) / 86_400_000
    );
    return { asOf, oldest, gapDays, evictedFeed: true };
  };

  // Size label reflects the zone as it is NOW, not as it was drawn.
  const getCurrentSizeLabel = (zone: Zone): string | null => {
    const count = getRecallCount(zone);
    return count == null ? null : getZoneSizeLabel(count, zone.oem);
  };

  // Only surfaced when the size category actually moved since creation.
  const getCreationSizeLabel = (zone: Zone): string | null => {
    if (zone.total_recalls_at_creation == null) return null;
    const creationLabel = getZoneSizeLabel(zone.total_recalls_at_creation, zone.oem);
    return creationLabel === getCurrentSizeLabel(zone) ? null : creationLabel;
  };

  // Calculate pagination info
  const startIndex = (page - 1) * ZONES_PER_PAGE;
  const endIndex = Math.min(startIndex + zones.length, totalZones);

  // Zone mutations with React Query (handles cache updates automatically)
  const {
    createZone: createZoneMutation,
    updateZone: updateZoneMutation,
    deleteZone: deleteZoneMutation
  } = useZoneMutations();
  const refreshRecallsMutation = useRefreshZoneRecallCounts();
  const isSuperAdmin = hasSystemRole(SystemRole.SUPER_ADMIN);

  // Derive available regions from the already-cached allZones array — no extra RPC.
  const availableRegions = useMemo(
    () => [...new Set(allZones.map(z => z.region).filter(Boolean))].sort() as string[],
    [allZones]
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [originalZipCodes, setOriginalZipCodes] = useState<string[]>([]); // Store original zip codes for comparison
  const [formLoading, setFormLoading] = useState(false);
  const [newZipCode, setNewZipCode] = useState('');
  const [zipCodeProgress, setZipCodeProgress] = useState<string | null>(null);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [zoneToDelete, setZoneToDelete] = useState<Zone | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Form state
  const [formData, setFormData] = useState<{
    zone_name: string;
    zone_slug: string;
    description: string;
    region: string;
    country: string;
    status: string;
    oem: string;
    zip_codes: string[];
  }>({
    zone_name: '',
    zone_slug: '',
    description: '',
    region: '',
    country: 'US',
    status: 'active',
    oem: '',
    zip_codes: []
  });

  // Preview recall count state
  const [previewRecallCount, setPreviewRecallCount] = useState<number | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Calculate preview recall count when OEM or ZIP codes change
  useEffect(() => {
    // Only calculate if we have both OEM and ZIP codes
    if (!formData.oem || formData.zip_codes.length === 0) {
      setPreviewRecallCount(null);
      return;
    }

    // Debounce the calculation
    const timeoutId = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const result = await getRecallCountFromRPC(formData.zip_codes, formData.oem);
        setPreviewRecallCount(result?.totalRecalls ?? 0);
      } catch (error) {
        console.error('[ZoneSetupTab] Failed to calculate preview recall count:', error);
        setPreviewRecallCount(null);
      } finally {
        setPreviewLoading(false);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [formData.oem, formData.zip_codes]);

  // Pagination handlers
  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(prev => prev + 1);
    }
  };

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(parseInt(newSize));
    setPage(1);
  };

  const handleRefreshRecalls = async () => {
    toast({
      title: 'Recounting zones',
      description: 'This may take a minute. You can keep working.'
    });

    try {
      await refreshRecallsMutation.mutateAsync();
      // Deliberately not "counts updated". This recounts every zone and stores the result,
      // but the number in the table comes from a live per-page query, so the displayed
      // value usually does not move — and promising it did is how an admin concludes the
      // refresh is broken. It also cannot make the data newer: the counts follow the crawl
      // schedule, so a zone whose "as of" date looks old stays that way until it is
      // crawled again.
      toast({
        title: 'Zones recounted',
        description: 'Stored counts are up to date with the latest crawls.'
      });
    } catch (error) {
      toast({
        title: 'Refresh failed',
        description: error instanceof Error ? error.message : 'Unable to refresh recall counts.',
        variant: 'destructive'
      });
    }
  };

  const openCreateDialog = () => {
    setEditingZone(null);
    setOriginalZipCodes([]);
    setFormData({
      zone_name: '',
      zone_slug: '',
      description: '',
      region: '',
      country: 'US',
      status: 'active',
      oem: '',
      zip_codes: []
    });
    setDialogOpen(true);
  };

  const openEditDialog = async (zone: Zone) => {
    setEditingZone(zone);

    // Load zip codes for this zone using React Query fetchQuery
    // This will use cached data if available, or fetch and cache if not
    let zipCodes: string[] = [];
    try {
      // eslint-disable-next-line no-console
      console.log(
        `[openEditDialog] Loading zip codes for zone: ${zone.zone_name} (ID: ${zone.id})`
      );

      // Use fetchQuery to get zip codes with proper React Query caching
      // This will check cache first, then fetch if needed, and cache the result
      zipCodes = await queryClient.fetchQuery({
        queryKey: zoneKeys.zipCodes(zone.id),
        queryFn: () => tenantAPI.getZoneZipCodes(zone.id),
        staleTime: 15 * 60 * 1000 // 15 minutes - same as useZoneZipCodes hook
      });

      // eslint-disable-next-line no-console
      console.log(
        `[openEditDialog] Successfully loaded ${zipCodes.length} zip codes for zone ${zone.zone_name}`
      );
    } catch (error) {
      // Log full error details for debugging
      // eslint-disable-next-line no-console
      console.error('[openEditDialog] Error loading zip codes:', {
        error,
        zoneId: zone.id,
        zoneName: zone.zone_name,
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined
      });

      // Show specific error messages based on error type
      let errorTitle = 'Warning';
      let errorDescription =
        'Failed to load zip codes for this zone. You can still view and edit other zone properties.';

      if (error instanceof Error) {
        if (
          error.message.includes('RPC function not found') ||
          error.message.includes('Could not find the function')
        ) {
          errorTitle = 'Database Migration Required';
          errorDescription =
            'The database function for reading zone zip codes is not available. ' +
            'Please run: migrations/add-get-zone-zip-codes-rpc-function.sql';
        } else if (
          error.message.includes('Access denied') ||
          error.message.includes('RLS') ||
          error.message.includes('permission')
        ) {
          errorTitle = 'Access Denied';
          errorDescription =
            'Unable to load zip codes due to access restrictions. ' +
            'Please check your permissions or contact an administrator.';
        } else if (error.message.includes('network') || error.message.includes('fetch')) {
          errorTitle = 'Network Error';
          errorDescription =
            'Failed to load zip codes due to a network issue. ' +
            'Please check your connection and try again.';
        }
      }

      toast({
        title: errorTitle,
        description: errorDescription,
        variant: 'destructive'
      });

      // Continue with empty array - user can still edit other fields
      zipCodes = [];
    }

    // Store original zip codes for comparison during update
    setOriginalZipCodes([...zipCodes]);

    setFormData({
      zone_name: zone.zone_name || '',
      zone_slug: zone.zone_slug || '',
      description: zone.description || '',
      region: zone.region || '',
      country: zone.country || 'US',
      status: zone.status || 'active',
      oem: zone.oem || '',
      zip_codes: zipCodes
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.zone_name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Zone name is required',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.oem) {
      toast({
        title: 'Validation Error',
        description: 'OEM is required',
        variant: 'destructive'
      });
      return;
    }

    try {
      setFormLoading(true);
      setZipCodeProgress(null);

      if (editingZone) {
        // Compare current formData zip codes with original zip codes from when dialog opened
        const currentZipCodes = formData.zip_codes || [];
        const zipCodesChanged =
          originalZipCodes.length !== currentZipCodes.length ||
          !originalZipCodes.every(zip => currentZipCodes.includes(zip)) ||
          !currentZipCodes.every(zip => originalZipCodes.includes(zip));

        const updateData: UpdateZoneData = {
          zone_name: formData.zone_name.trim(),
          zone_slug: formData.zone_slug.trim() || undefined,
          description: formData.description || undefined,
          region: formData.region || undefined,
          country: formData.country || undefined,
          status: formData.status,
          oem: formData.oem,
          // Only include zip_codes if they changed to avoid unnecessary validation
          ...(zipCodesChanged ? { zip_codes: formData.zip_codes } : {})
        };

        // Show progress for large ZIP code updates
        if (zipCodesChanged && formData.zip_codes.length > 50) {
          setZipCodeProgress(`Updating ${formData.zip_codes.length} ZIP codes...`);
        }

        // Use React Query mutation (handles cache updates automatically)
        await updateZoneMutation.mutateAsync({
          id: editingZone.id,
          data: updateData,
          userId: session?.user?.id
        });

        toast({
          title: 'Zone Updated',
          description: `Zone "${formData.zone_name}" has been updated successfully`
        });
      } else {
        const createData: CreateZoneData = {
          zone_name: formData.zone_name.trim(),
          zone_slug: formData.zone_slug.trim() || undefined,
          description: formData.description || undefined,
          region: formData.region || undefined,
          country: formData.country || undefined,
          status: formData.status,
          oem: formData.oem,
          zip_codes: formData.zip_codes
        };

        // Show progress for large ZIP code operations
        if (formData.zip_codes.length > 50) {
          setZipCodeProgress(`Creating zone with ${formData.zip_codes.length} ZIP codes...`);
        }

        // Use React Query mutation (handles cache updates automatically)
        await createZoneMutation.mutateAsync({
          data: createData,
          userId: session?.user?.id
        });

        toast({
          title: 'Zone Created',
          description: `Zone "${formData.zone_name}" has been created successfully`
        });
      }

      setDialogOpen(false);
      setZipCodeProgress(null);

      // Invalidate regions query in case a new region was added
      if (formData.region && !availableRegions.includes(formData.region)) {
        queryClient.invalidateQueries({ queryKey: ['zones', 'regions'] });
      }

      // Reset to page 1 if creating a new zone
      if (!editingZone) {
        setPage(1);
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || `Failed to ${editingZone ? 'update' : 'create'} zone`,
        variant: 'destructive'
      });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = (zone: Zone) => {
    setZoneToDelete(zone);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!zoneToDelete) return;

    try {
      setDeleting(true);

      // Use React Query mutation (handles cache updates automatically)
      await deleteZoneMutation.mutateAsync({
        id: zoneToDelete.id,
        hard: true,
        userId: session?.user?.id
      });

      toast({
        title: 'Zone Deleted',
        description: `Zone "${zoneToDelete.zone_name}" has been deleted permanently`
      });

      setDeleteDialogOpen(false);
      setZoneToDelete(null);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete zone',
        variant: 'destructive'
      });
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setZoneToDelete(null);
  };

  const addZipCode = () => {
    const zip = newZipCode.trim();
    if (zip && !formData.zip_codes.includes(zip)) {
      setFormData(prev => ({
        ...prev,
        zip_codes: [...prev.zip_codes, zip]
      }));
      setNewZipCode('');
    }
  };

  const removeZipCode = (zipCode: string) => {
    setFormData(prev => ({
      ...prev,
      zip_codes: prev.zip_codes.filter(z => z !== zipCode)
    }));
  };

  const handleZipCodeKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addZipCode();
    }
  };

  // Note: Filtering is now done server-side via the API
  // The zones returned are already filtered based on searchTerm, statusFilter, and regionFilter

  // Toggle zone selection
  const toggleZoneSelection = (zoneId: string) => {
    setSelectedZones(prev => {
      const newSet = new Set(prev);
      if (newSet.has(zoneId)) {
        newSet.delete(zoneId);
      } else {
        newSet.add(zoneId);
      }
      return newSet;
    });
  };

  // Toggle select all (for current page only)
  const toggleSelectAll = () => {
    const currentPageZoneIds = new Set(zones.map(z => z.id));
    const allSelected = zones.length > 0 && zones.every(z => selectedZones.has(z.id));

    if (allSelected) {
      // Deselect all zones on current page
      setSelectedZones(prev => {
        const newSet = new Set(prev);
        currentPageZoneIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    } else {
      // Select all zones on current page
      setSelectedZones(prev => {
        const newSet = new Set(prev);
        currentPageZoneIds.forEach(id => newSet.add(id));
        return newSet;
      });
    }
  };

  // Export selected zones to Excel
  const handleExportZones = async () => {
    if (selectedZones.size === 0 || exporting) {
      if (selectedZones.size === 0) {
        toast({
          title: 'No zones selected',
          description: 'Please select at least one zone to export',
          variant: 'destructive'
        });
      }
      return;
    }

    try {
      setExporting(true);

      // Get selected zone objects from all zones (not just filtered)
      const zonesToExport = zones.filter(z => selectedZones.has(z.id));

      // Fetch zip codes for each selected zone
      const exportData = await Promise.all(
        zonesToExport.map(async zone => {
          const zipCodes = await tenantAPI.getZoneZipCodes(zone.id);

          return {
            'Zone Name': zone.zone_name || '',
            Region: zone.region || '',
            'Zip Codes': zipCodes.join(', ')
          };
        })
      );

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      worksheet['!cols'] = [
        { wch: 20 }, // Zone Name
        { wch: 15 }, // Region
        { wch: 50 } // Zip Codes
      ];

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Zones');

      // Generate Excel file and download
      const fileName = `zones-export-${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      toast({
        title: 'Export successful',
        description: `Exported ${zonesToExport.length} zone(s) to ${fileName}`
      });

      // Clear selection after export
      setSelectedZones(new Set());
    } catch (error: any) {
      toast({
        title: 'Export failed',
        description: error.message || 'Failed to export zones',
        variant: 'destructive'
      });
    } finally {
      setExporting(false);
    }
  };

  // Export zip codes for a single zone
  const handleExportSingleZone = async (zone: Zone) => {
    setLoadingZipCodes(prev => new Set(prev).add(zone.id));
    try {
      const zipCodes = await queryClient.fetchQuery({
        queryKey: zoneKeys.zipCodes(zone.id),
        queryFn: () => tenantAPI.getZoneZipCodes(zone.id),
        staleTime: 15 * 60 * 1000
      });

      const exportData = [{ 'Zone Name': zone.zone_name || '', 'Zip Codes': zipCodes.join(', ') }];
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      worksheet['!cols'] = [{ wch: 20 }, { wch: 80 }];
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Zip Codes');
      XLSX.writeFile(workbook, `${zone.zone_slug || zone.zone_name}-zip-codes.xlsx`);

      toast({ title: 'Export successful', description: `Exported ${zipCodes.length} zip codes` });
    } catch (error: any) {
      toast({
        title: 'Export failed',
        description: error.message || 'Failed to export zip codes',
        variant: 'destructive'
      });
    } finally {
      setLoadingZipCodes(prev => {
        const next = new Set(prev);
        next.delete(zone.id);
        return next;
      });
    }
  };

  // Copy zip codes to clipboard for a single zone
  const handleCopyZipCodes = async (zone: Zone) => {
    setLoadingZipCodes(prev => new Set(prev).add(zone.id));
    try {
      const zipCodes = await queryClient.fetchQuery({
        queryKey: zoneKeys.zipCodes(zone.id),
        queryFn: () => tenantAPI.getZoneZipCodes(zone.id),
        staleTime: 15 * 60 * 1000
      });

      await navigator.clipboard.writeText(zipCodes.join(', '));
      toast({ title: 'Copied to clipboard', description: `${zipCodes.length} zip codes copied` });
    } catch (error: any) {
      toast({
        title: 'Copy failed',
        description: error.message || 'Failed to copy zip codes',
        variant: 'destructive'
      });
    } finally {
      setLoadingZipCodes(prev => {
        const next = new Set(prev);
        next.delete(zone.id);
        return next;
      });
    }
  };

  // Show error if zones query failed
  if (zonesError) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <AlertTriangle className='h-8 w-8 mx-auto mb-2 text-destructive' />
          <p className='text-destructive'>
            {zonesError instanceof Error ? zonesError.message : 'Failed to load zones'}
          </p>
        </div>
      </div>
    );
  }

  if (loading && zones.length === 0) {
    return (
      <div className='flex items-center justify-center h-64'>
        <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
      </div>
    );
  }

  return (
    <div className='flex h-full w-full flex-col overflow-hidden'>
      {/* KPI strip — Total / Assigned / Unassigned cards plus an OEM
          distribution stacked bar so the admin gets at-a-glance answers to
          "how many territories do we have, how many are subscribed, and
          which brands dominate?" without scrolling the table. */}
      <div className='grid grid-cols-1 lg:grid-cols-[repeat(3,minmax(0,1fr))_1.5fr] gap-3 px-8 py-4 border-b flex-shrink-0 bg-muted/30'>
        <StatCard
          title='Total Zones'
          value={totalZones}
          icon={MapPin}
          loading={loading}
          description={
            statusFilter === 'all'
              ? `${activeZones.length} active`
              : `Filtered ${statusFilter}`
          }
        />
        <StatCard
          title='Assigned'
          value={assignedZoneCount}
          icon={Building2}
          loading={loading || !tenantMappings}
          description='Mapped to a tenant'
        />
        <StatCard
          title='Unassigned'
          value={unassignedZoneCount}
          icon={CircleSlash}
          loading={loading || !tenantMappings}
          description='Active, no tenant'
        />

        {/* OEM distribution: horizontal stacked bar. Each segment width is
            proportional to that OEM's share of active zones. Hovering shows
            an exact count via title attribute. */}
        <div className='flex flex-col justify-between rounded-lg border bg-card p-4 min-h-[88px]'>
          <div className='flex items-center justify-between text-xs font-medium text-muted-foreground mb-2'>
            <span>Zones per OEM</span>
            <span className='tabular-nums'>{oemDistribution.length} OEM{oemDistribution.length === 1 ? '' : 's'}</span>
          </div>
          {oemDistribution.length === 0 ? (
            <div className='text-xs text-muted-foreground italic'>No active zones yet</div>
          ) : (
            <>
              <div className='flex h-6 w-full overflow-hidden rounded-md border'>
                {oemDistribution.map(({ oem, count, percent, color }) => (
                  <div
                    key={oem}
                    className='h-full transition-opacity hover:opacity-80'
                    style={{
                      width: `${percent}%`,
                      backgroundColor: color.fill,
                      borderRight: `1px solid ${color.stroke}`
                    }}
                    title={`${oem}: ${count} zone${count === 1 ? '' : 's'} (${percent.toFixed(1)}%)`}
                  />
                ))}
              </div>
              <div className='mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground'>
                {oemDistribution.slice(0, 6).map(({ oem, count, color }) => (
                  <span key={oem} className='inline-flex items-center gap-1'>
                    <span
                      className='inline-block h-2 w-2 rounded-sm'
                      style={{ backgroundColor: color.fill }}
                    />
                    <span className='font-medium text-foreground'>{oem}</span>
                    <span className='tabular-nums'>{count}</span>
                  </span>
                ))}
                {oemDistribution.length > 6 && (
                  <span className='italic'>+ {oemDistribution.length - 6} more</span>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Compact toolbar — filters + view toggle + actions in one row */}
      <div className='flex flex-wrap items-end gap-4 px-8 py-4 border-b flex-shrink-0'>
        <div className='space-y-1 flex-1 min-w-[220px] max-w-md'>
          <label className='text-xs font-medium text-muted-foreground'>Search</label>
          <div className='relative'>
            <Search className='absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
            <Input
              placeholder='Search zones...'
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className='pl-8 h-9'
            />
          </div>
        </div>

        <div className='space-y-1'>
          <label className='text-xs font-medium text-muted-foreground'>OEM</label>
          <Select value={oemFilter} onValueChange={setOemFilter}>
            <SelectTrigger className='w-[150px] h-9'>
              <SelectValue placeholder='All OEMs' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All OEMs</SelectItem>
              {KNOWN_OEMS.filter(oem => oem !== 'Other').map(oem => (
                <SelectItem key={oem} value={oem}>
                  {oem}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className='space-y-1'>
          <label className='text-xs font-medium text-muted-foreground'>Region</label>
          <Select value={regionFilter} onValueChange={setRegionFilter}>
            <SelectTrigger className='w-[150px] h-9'>
              <SelectValue placeholder='All Regions' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Regions</SelectItem>
              {availableRegions.map(region => (
                <SelectItem key={region} value={region}>
                  {region}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className='space-y-1'>
          <label className='text-xs font-medium text-muted-foreground'>Status</label>
          <Select value={statusFilter} onValueChange={v => setStatusFilter(v as any)}>
            <SelectTrigger className='w-[140px] h-9'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Statuses</SelectItem>
              <SelectItem value='active'>Active</SelectItem>
              <SelectItem value='inactive'>Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className='flex-1' />

        <span className='text-xs text-muted-foreground self-end pb-2.5'>
          {totalZones.toLocaleString()} zone{totalZones === 1 ? '' : 's'}
        </span>

        {/* View toggle (only when parent passes onViewChange) */}
        {onViewChange && (
          <div className='flex items-center bg-muted p-1 rounded-md h-9 self-end'>
            <Button
              variant={view === 'list' ? 'secondary' : 'ghost'}
              size='sm'
              className='h-7 w-7 p-0'
              onClick={() => onViewChange('list')}
              title='List view'
              aria-label='List view'
            >
              <List className='h-4 w-4' />
            </Button>
            <Button
              variant={view === 'map' ? 'secondary' : 'ghost'}
              size='sm'
              className='h-7 w-7 p-0'
              onClick={() => onViewChange('map')}
              title='Map view'
              aria-label='Map view'
            >
              <MapIcon className='h-4 w-4' />
            </Button>
          </div>
        )}

        {isSuperAdmin && (
          <Button
            variant='ghost'
            size='sm'
            className='h-9 w-9 p-0 self-end'
            onClick={handleRefreshRecalls}
            disabled={refreshRecallsMutation.isPending}
            title='Refresh Recalls'
            aria-label='Refresh recalls'
          >
            {refreshRecallsMutation.isPending ? (
              <Loader2 className='h-4 w-4 animate-spin' />
            ) : (
              <RefreshCw className='h-4 w-4' />
            )}
          </Button>
        )}

        <Button
          onClick={handleExportZones}
          disabled={exporting || selectedZones.size === 0}
          variant='outline'
          className='h-9 self-end'
        >
          {exporting ? (
            <>
              <Loader2 className='h-4 w-4 mr-1 animate-spin' />
              Exporting...
            </>
          ) : (
            <>
              <Download className='h-4 w-4 mr-1' />
              {selectedZones.size > 0 ? `Export (${selectedZones.size})` : 'Export'}
            </>
          )}
        </Button>

        <Button onClick={openCreateDialog} className='h-9 self-end'>
          <Plus className='h-4 w-4 mr-1' />
          Add Zone
        </Button>
      </div>

      {/* Table area */}
      <div className='flex-1 min-h-0 overflow-y-auto px-8 py-6'>
          {zones.length === 0 && !loading ? (
            <div className='text-center py-12 text-muted-foreground'>
              <MapPin className='h-12 w-12 mx-auto mb-4 opacity-50' />
              <p>No zones found</p>
              {totalZones > 0 && <p className='text-sm mt-2'>Try adjusting your filters</p>}
            </div>
          ) : (
            <>
              <div className='overflow-x-auto'>
                <Table>
                  <TableHeader className='sticky top-0 z-10 bg-card shadow-sm'>
                    <TableRow>
                      <TableHead className='w-12'>
                        <Checkbox
                          checked={zones.length > 0 && zones.every(z => selectedZones.has(z.id))}
                          onCheckedChange={toggleSelectAll}
                          aria-label='Select all zones'
                        />
                      </TableHead>
                      <TableHead className='w-12 text-center'>#</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>OEM</TableHead>
                      <TableHead>Zone Size</TableHead>
                      <TableHead>Recalls (Creation)</TableHead>
                      <TableHead>Recalls (Current)</TableHead>
                      <TableHead>Tenant</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className='w-10'>Info</TableHead>
                      <TableHead className='w-10'>Export</TableHead>
                      <TableHead className='w-10'>Copy</TableHead>
                      <TableHead className='w-10'>Edit</TableHead>
                      <TableHead className='w-10'>Delete</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {zones.map((zone, idx) => (
                      <TableRow
                        key={zone.id}
                        className={
                          tenantMappings && !tenantMappings[zone.id] ? 'opacity-60' : ''
                        }
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedZones.has(zone.id)}
                            onCheckedChange={() => toggleZoneSelection(zone.id)}
                            aria-label={`Select ${zone.zone_name}`}
                          />
                        </TableCell>
                        <TableCell className='text-center text-xs text-muted-foreground tabular-nums'>
                          {startIndex + idx + 1}
                        </TableCell>
                        <TableCell className='font-medium'>{zone.zone_name}</TableCell>
                        <TableCell>
                          {/* OEM badge uses the same Vehix OEM color palette
                              as the OEM-distribution stacked bar above and
                              the OEM badges across other admin pages — Ford
                              is always blue, Toyota is always red, etc.
                              Tailwind classes from getOemTheme include
                              dark-mode variants so the badge reads in both
                              themes. */}
                          <Badge variant='outline' className={getOemTheme(zone.oem).className}>
                            {zone.oem}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {getRecallCount(zone) != null ? (
                            <div className='flex flex-col gap-0.5'>
                              <Badge
                                className='text-white border-0 w-fit'
                                style={{
                                  backgroundColor: getZoneColor(getRecallCount(zone)!, zone.oem)
                                }}
                              >
                                {getCurrentSizeLabel(zone)}
                              </Badge>
                              {getCreationSizeLabel(zone) && (
                                <span className='text-[10px] text-muted-foreground'>
                                  was {getCreationSizeLabel(zone)}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className='text-muted-foreground'>—</span>
                          )}
                        </TableCell>
                        <TableCell className='font-medium'>
                          {zone.total_recalls_at_creation ?? '—'}
                        </TableCell>
                        <TableCell>
                          <div className='flex flex-col gap-0.5'>
                            <span
                              className={`font-medium ${
                                getRecallCount(zone) != null
                                  ? getRecallCount(zone)! > (zone.total_recalls_at_creation || 0)
                                    ? 'text-red-600'
                                    : 'text-green-600'
                                  : ''
                              }`}
                            >
                              {getRecallCount(zone) ?? '—'}
                            </span>
                            {(() => {
                              const crawl = getCrawlWindow(zone);
                              if (!crawl) return null;
                              return (
                                <span
                                  className='inline-flex items-center gap-1 text-[10px] text-muted-foreground'
                                  title={
                                    crawl.evictedFeed
                                      ? `Counted as of ${crawl.asOf} — the newest crawl for this zone's tenant. This zone is covered by more than one crawl and the oldest still counted is ${crawl.oldest}, ${crawl.gapDays} days earlier, so part of its territory is counted from older data or not at all.`
                                      : `Counted as of ${crawl.asOf} — the newest crawl for this zone's tenant. All of this zone's territory is at that one crawl.`
                                  }
                                >
                                  {crawl.evictedFeed && (
                                    <AlertTriangle className='h-2.5 w-2.5 flex-shrink-0' />
                                  )}
                                  as of {crawl.asOf}
                                  {crawl.evictedFeed && ` · oldest ${crawl.oldest}`}
                                </span>
                              );
                            })()}
                          </div>
                        </TableCell>
                        <TableCell>
                          {tenantMappings && tenantMappings[zone.id] ? (
                            <span className='text-xs font-medium'>
                              {tenantMappings[zone.id]}
                            </span>
                          ) : (
                            <span className='text-xs text-muted-foreground italic'>Unmapped</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {zone.status === 'active' ? (
                            <Badge variant='default' className='bg-green-500'>
                              Active
                            </Badge>
                          ) : (
                            <Badge variant='secondary'>Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant='ghost' size='sm' className='h-8 w-8 p-0'>
                                <Info className='h-4 w-4' />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent side='left' className='w-72'>
                              <div className='space-y-3 text-sm'>
                                <div className='flex items-center justify-between'>
                                  <span className='text-muted-foreground'>Slug</span>
                                  <code className='text-xs bg-muted px-2 py-0.5 rounded'>
                                    {zone.zone_slug || 'N/A'}
                                  </code>
                                </div>
                                <div className='flex items-center justify-between'>
                                  <span className='text-muted-foreground'>Region</span>
                                  <span className='font-medium'>{zone.region || 'N/A'}</span>
                                </div>
                                <div className='flex items-center justify-between'>
                                  <span className='text-muted-foreground'>Country</span>
                                  <span className='font-medium'>{zone.country || 'US'}</span>
                                </div>
                                <div className='flex items-center justify-between'>
                                  <span className='text-muted-foreground'>Zip Codes</span>
                                  <Badge variant='outline' className='text-xs'>
                                    <Hash className='h-3 w-3 mr-1' />
                                    {zone.zip_code_count || 0}
                                  </Badge>
                                </div>
                                <div className='border-t pt-2 mt-1'>
                                  <div className='flex items-center justify-between'>
                                    <span className='text-muted-foreground'>Created</span>
                                    <span className='text-xs'>
                                      {zone.created_at
                                        ? new Date(zone.created_at).toLocaleDateString()
                                        : 'N/A'}
                                    </span>
                                  </div>
                                  <div className='flex items-center justify-between mt-1'>
                                    <span className='text-muted-foreground'>Updated</span>
                                    <span className='text-xs'>
                                      {zone.updated_at
                                        ? new Date(zone.updated_at).toLocaleDateString()
                                        : 'N/A'}
                                    </span>
                                  </div>
                                </div>
                                {(() => {
                                  const dcZones = dataCollectionZones?.[zone.id];
                                  if (!dcZones || dcZones.length === 0) return null;
                                  return (
                                    <div className='border-t pt-2 mt-1'>
                                      <span className='text-muted-foreground text-xs'>
                                        Data Collection Zones
                                      </span>
                                      <div className='flex flex-wrap gap-1 mt-1'>
                                        {dcZones.map(dcZone => (
                                          <Badge
                                            key={dcZone}
                                            variant='outline'
                                            className='text-[10px] px-1.5 py-0'
                                          >
                                            {dcZone}
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            </PopoverContent>
                          </Popover>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant='ghost'
                            size='sm'
                            className='h-8 w-8 p-0'
                            onClick={() => handleExportSingleZone(zone)}
                            disabled={loadingZipCodes.has(zone.id)}
                            title='Export zip codes'
                          >
                            {loadingZipCodes.has(zone.id) ? (
                              <Loader2 className='h-4 w-4 animate-spin' />
                            ) : (
                              <Download className='h-4 w-4' />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant='ghost'
                            size='sm'
                            className='h-8 w-8 p-0'
                            onClick={() => handleCopyZipCodes(zone)}
                            disabled={loadingZipCodes.has(zone.id)}
                            title='Copy zip codes to clipboard'
                          >
                            {loadingZipCodes.has(zone.id) ? (
                              <Loader2 className='h-4 w-4 animate-spin' />
                            ) : (
                              <Copy className='h-4 w-4' />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant='ghost'
                            size='sm'
                            className='h-8 w-8 p-0'
                            onClick={() => openEditDialog(zone)}
                          >
                            <Edit className='h-4 w-4' />
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant='ghost'
                            size='sm'
                            onClick={() => handleDelete(zone)}
                            className='text-destructive hover:text-destructive h-8 w-8 p-0'
                          >
                            <Trash2 className='h-4 w-4' />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination Controls */}
              {totalZones > 0 && (
                <div className='flex items-center justify-between border-t pt-4 mt-4'>
                  <div className='text-sm text-muted-foreground'>
                    Showing <span className='font-medium text-foreground'>{startIndex + 1}</span> to{' '}
                    <span className='font-medium text-foreground'>{endIndex}</span> of{' '}
                    <span className='font-medium text-foreground'>{totalZones}</span> zones
                  </div>
                  <div className='flex items-center gap-2'>
                    <div className='flex items-center gap-1.5 text-sm text-muted-foreground'>
                      <span>Rows</span>
                      <Select
                        value={String(pageSize)}
                        onValueChange={handlePageSizeChange}
                      >
                        <SelectTrigger className='h-8 w-[80px]'>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='25'>25</SelectItem>
                          <SelectItem value='50'>50</SelectItem>
                          <SelectItem value='100'>100</SelectItem>
                          {/* 200 is the ceiling on purpose. There used to be an "All"
                              option that set pageSize to the full zone count, which sent
                              every zone id to the recall-count RPC in one request — ~24s
                              for the whole fleet, and the server's statement timeout cut
                              roughly one in seven of those off. Paging keeps each request
                              to a size that actually returns. */}
                          <SelectItem value='200'>200</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={handlePreviousPage}
                      disabled={page <= 1 || loading}
                    >
                      <ChevronLeft className='h-4 w-4 mr-1' />
                      Previous
                    </Button>
                    <div className='flex items-center gap-1'>
                      <span className='text-sm text-muted-foreground'>Page</span>
                      <span className='text-sm font-medium text-foreground'>{page}</span>
                      <span className='text-sm text-muted-foreground'>of</span>
                      <span className='text-sm font-medium text-foreground'>{totalPages || 1}</span>
                    </div>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={handleNextPage}
                      disabled={page >= totalPages || loading || !hasMore}
                    >
                      Next
                      <ChevronRight className='h-4 w-4 ml-1' />
                    </Button>
                  </div>
                </div>
              )}

              {/* Loading indicator for pagination */}
              {loadingMore && (
                <div className='flex items-center justify-center py-4'>
                  <Loader2 className='h-4 w-4 animate-spin text-muted-foreground mr-2' />
                  <span className='text-sm text-muted-foreground'>Loading zones...</span>
                </div>
              )}
            </>
          )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className='max-w-4xl max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>{editingZone ? 'Edit Zone' : 'Create New Zone'}</DialogTitle>
            <DialogDescription>
              {editingZone
                ? 'Update zone configuration and zip code mappings'
                : 'Create a new zone and map zip codes'}
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-4 py-4'>
            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='zone_name'>
                  Zone Name <span className='text-destructive'>*</span>
                </Label>
                <Input
                  id='zone_name'
                  value={formData.zone_name}
                  onChange={e => setFormData(prev => ({ ...prev, zone_name: e.target.value }))}
                  placeholder='e.g., US East'
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='zone_slug'>Slug</Label>
                <Input
                  id='zone_slug'
                  value={formData.zone_slug}
                  onChange={e => setFormData(prev => ({ ...prev, zone_slug: e.target.value }))}
                  placeholder='e.g., us-east'
                />
              </div>
            </div>

            <div className='grid grid-cols-4 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='oem'>
                  OEM <span className='text-destructive'>*</span>
                </Label>
                <Select
                  value={formData.oem}
                  onValueChange={value => setFormData(prev => ({ ...prev, oem: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select OEM' />
                  </SelectTrigger>
                  <SelectContent>
                    {KNOWN_OEMS.filter(oem => oem !== 'Other').map(oem => (
                      <SelectItem key={oem} value={oem}>
                        {oem}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className='space-y-2'>
                <Label htmlFor='region'>Region</Label>
                <Input
                  id='region'
                  value={formData.region}
                  onChange={e => setFormData(prev => ({ ...prev, region: e.target.value }))}
                  placeholder='e.g., Northeast'
                />
                <p className='text-xs text-muted-foreground'>
                  Multiple zones can share the same region
                </p>
              </div>
              <div className='space-y-2'>
                <Label htmlFor='country'>Country</Label>
                <Input
                  id='country'
                  value={formData.country}
                  onChange={e => setFormData(prev => ({ ...prev, country: e.target.value }))}
                  placeholder='e.g., US'
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='status'>Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={value => setFormData(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='active'>Active</SelectItem>
                    <SelectItem value='inactive'>Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='description'>Description</Label>
              <Textarea
                id='description'
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder='Zone description...'
                rows={2}
              />
            </div>

            <div className='space-y-2 border-t pt-4'>
              <div className='flex items-center justify-between mb-2'>
                <Label>Zip Codes</Label>
                <div className='text-sm text-muted-foreground'>
                  {formData.zip_codes.length} zip code{formData.zip_codes.length !== 1 ? 's' : ''}{' '}
                  mapped
                </div>
              </div>

              {/* Preview Recall Count */}
              {formData.oem && formData.zip_codes.length > 0 && (
                <div className='flex items-center gap-2 p-3 bg-muted/50 rounded-md mb-4'>
                  <Info className='h-4 w-4 text-muted-foreground flex-shrink-0' />
                  <div className='text-sm'>
                    <span className='text-muted-foreground'>
                      Estimated recalls for {formData.oem}:{' '}
                    </span>
                    {previewLoading ? (
                      <span className='inline-flex items-center gap-1'>
                        <Loader2 className='h-3 w-3 animate-spin' />
                        Calculating...
                      </span>
                    ) : previewRecallCount !== null ? (
                      <span className='font-semibold text-foreground'>
                        {previewRecallCount.toLocaleString()}
                      </span>
                    ) : (
                      <span className='text-muted-foreground'>—</span>
                    )}
                  </div>
                </div>
              )}

              {/* Add Zip Code Input */}
              <div className='flex gap-2 mb-4'>
                <Input
                  placeholder='Enter zip code (e.g., 33612)'
                  value={newZipCode}
                  onChange={e => setNewZipCode(e.target.value)}
                  onKeyPress={handleZipCodeKeyPress}
                  className='flex-1'
                />
                <Button
                  type='button'
                  onClick={addZipCode}
                  disabled={!newZipCode.trim() || formData.zip_codes.includes(newZipCode.trim())}
                >
                  <Plus className='h-4 w-4 mr-2' />
                  Add
                </Button>
              </div>

              {/* List of Mapped Zip Codes */}
              {formData.zip_codes.length === 0 ? (
                <div className='border rounded-md p-8 text-center'>
                  <p className='text-sm text-muted-foreground'>
                    No zip codes mapped yet. Add zip codes using the input above.
                  </p>
                </div>
              ) : (
                <div className='border rounded-md p-4'>
                  <div className='flex flex-wrap gap-2'>
                    {formData.zip_codes.map(zip => (
                      <Badge
                        key={zip}
                        variant='secondary'
                        className='text-sm py-1 px-3 flex items-center gap-2'
                      >
                        <Hash className='h-3 w-3' />
                        {zip}
                        <button
                          onClick={() => removeZipCode(zip)}
                          className='ml-1 hover:text-destructive rounded-full p-0.5'
                          type='button'
                          aria-label={`Remove ${zip}`}
                        >
                          <X className='h-3 w-3' />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <div className='flex items-center justify-between w-full'>
              {zipCodeProgress && (
                <div className='flex items-center text-sm text-muted-foreground'>
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                  {zipCodeProgress}
                </div>
              )}
              <div className='flex gap-2 ml-auto'>
                <Button
                  variant='outline'
                  onClick={() => setDialogOpen(false)}
                  disabled={formLoading}
                >
                  Cancel
                </Button>
                <Button onClick={handleSubmit} disabled={formLoading}>
                  {formLoading && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
                  {editingZone ? 'Update' : 'Create'} Zone
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className='sm:max-w-[500px]'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <AlertTriangle className='w-5 h-5 text-destructive' />
              Delete Zone
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete this zone? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {zoneToDelete && (
            <div className='py-4'>
              <div className='rounded-lg border bg-muted/30 p-4'>
                <div className='space-y-2'>
                  <div className='flex items-center justify-between'>
                    <span className='text-sm font-medium text-muted-foreground'>Zone Name</span>
                    <span className='font-semibold'>{zoneToDelete.zone_name}</span>
                  </div>

                  {zoneToDelete.region && (
                    <div className='flex items-center justify-between'>
                      <span className='text-sm font-medium text-muted-foreground'>Region</span>
                      <span>{zoneToDelete.region}</span>
                    </div>
                  )}

                  <div className='flex items-center justify-between'>
                    <span className='text-sm font-medium text-muted-foreground'>ZIP Codes</span>
                    <Badge variant='outline'>
                      <Hash className='h-3 w-3 mr-1' />
                      {zoneToDelete.zip_code_count || 0}
                    </Badge>
                  </div>

                  <div className='flex items-center justify-between'>
                    <span className='text-sm font-medium text-muted-foreground'>Status</span>
                    <Badge variant={zoneToDelete.status === 'active' ? 'default' : 'secondary'}>
                      {zoneToDelete.status === 'active' ? (
                        <>
                          <CheckCircle className='h-3 w-3 mr-1' />
                          Active
                        </>
                      ) : (
                        <>
                          <XCircle className='h-3 w-3 mr-1' />
                          Inactive
                        </>
                      )}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className='mt-4 p-3 bg-destructive/5 border border-destructive/20 rounded-lg'>
                <div className='flex items-start gap-2'>
                  <AlertTriangle className='w-4 h-4 text-destructive mt-0.5 flex-shrink-0' />
                  <div className='text-sm text-destructive'>
                    <p className='font-medium mb-1'>Warning: This is permanent</p>
                    <p>
                      This will permanently delete the zone and all its ZIP code associations. Any
                      references to this zone will be removed.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant='outline' onClick={cancelDelete} disabled={deleting}>
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={confirmDelete}
              disabled={deleting}
              className='min-w-24'
            >
              {deleting ? (
                <>
                  <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className='w-4 h-4 mr-2' />
                  Delete Zone
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

interface ZoneSetupTabProps {
  /** When provided, the inner toolbar renders a List/Map view-toggle on the right. */
  view?: 'list' | 'map';
  onViewChange?: (view: 'list' | 'map') => void;
}

export const ZoneSetupTab: React.FC<ZoneSetupTabProps> = ({ view, onViewChange }) => {
  return (
    <div className='flex h-full w-full flex-col overflow-hidden'>
      <ManualZoneTab
        {...(view !== undefined && { view })}
        {...(onViewChange !== undefined && { onViewChange })}
      />
    </div>
  );
};
